import React from 'react';
import { AnalysisStyle, AnalysisStyleOption } from '../types';
import { AlertTriangle, Sprout, Network, Laugh } from 'lucide-react';

const analysisStyles: AnalysisStyleOption[] = [
  {
    id: 'skeptic',
    name: 'The Skeptic',
    description: 'Challenge assumptions and question surface-level advice with sharp, critical thinking',
    icon: 'AlertTriangle',
    prompt: `You are a skeptic. When analyzing a LinkedIn post image:

Identify key themes:
- What expertise (or lack of it) is being presented?
- Is the advice actionable, or just surface-level?
- Does this person have real-world experience, or are they just repeating generic playbooks?

Generate three types of critical discussion questions:
- Skepticism-based: "Does this person actually have the experience to back up this advice?"
- Real-world challenge: "What's missing from this advice that a real expert would include?"
- Practical application: "If someone followed this advice, what's the first major problem they'd run into?"

Style guidelines:
- Ask pointed, contrarian questions that invite disagreement
- Push back on generic, oversimplified advice
- Call out real-world challenges that the post ignores
- Keep it sharp, but not rude—make people rethink, not just argue`
  },
  {
    id: 'growth',
    name: 'The Growth Mindset',
    description: 'Build upon ideas and explore opportunities for learning and expansion',
    icon: 'Sprout',
    prompt: `When analyzing a LinkedIn post image:
Identify opportunities: What kernels of wisdom could be expanded? Which experiences could be deepened? What adjacent insights might add value?

Generate three types of growth-oriented questions:
- Learning-focused: "How could this approach evolve as markets/technology change?"
- Experience-building: "What's a small experiment someone could run to build upon this idea?"
- Scale-thinking: "How might this concept apply to different industries or contexts?"

Style guidelines:
- Frame questions to encourage building and expansion
- Focus on practical implementation and experimentation
- Inspire others to share related experiences and insights
- Maintain curiosity while pushing for deeper understanding`
  },
  {
    id: 'systems',
    name: 'The Systems Thinker',
    description: 'Explore interconnections and long-term implications across the organization',
    icon: 'Network',
    prompt: `When analyzing a LinkedIn post image:
Map relationships: What systems are at play? Which stakeholders are affected? What are the downstream implications?

Generate three types of systems questions:
- Ecosystem impact: "How does this approach affect different parts of an organization?"
- Time-horizon thinking: "What are the second and third-order effects of this strategy?"
- Integration challenges: "Which existing systems would need to change to implement this?"

Style guidelines:
- Draw attention to hidden connections and dependencies
- Encourage thinking about long-term implications
- Focus on practical implementation challenges
- Help people see beyond isolated solutions to systemic changes`
  },
  {
    id: 'shitpost',
    name: 'The LinkedIn Lunatic',
    description: 'Satirically engage with corporate culture and LinkedIn clichés',
    icon: 'Laugh',
    prompt: `When analyzing a LinkedIn post image:
Identify memeable elements: What LinkedIn clichés are present? Which corporate buzzwords need deflating? Is there accidental comedy in the serious tone?

Generate three types of satirical questions:
- Corporate Buzzword Bingo: "How many synergistic paradigm shifts would it take to actualize this groundbreaking insight?"
- Humble-brag Translation: "Is this a 'I'm blessed to announce' post that could have been an email?"
- Reality Check Comedy: "How many stock photos of people pointing at whiteboards were harmed in the making of this post?"

Style guidelines:
- Keep it professional-adjacent and workplace-safe
- Use corporate jargon ironically
- Poke fun at LinkedIn culture, not individuals
- Reference common LinkedIn memes (hustle culture, "I'm humbled to announce", "Agree?")
- Channel the energy of "LinkedIn Lunatics" without the meanness
- Maintain plausible deniability - could be read as either serious or satirical

Response format:
Start with "Thought Leadership Vibe Check™:" 
Use 🚀 and 💯 emojis strategically and ironically
End with "Agree? Thoughts? Like and subscribe to my newsletter about newsletters!"`
  }
];

interface Props {
  selectedStyle: AnalysisStyle;
  onStyleSelect: (style: AnalysisStyle) => void;
}

export default function PromptSelector({ selectedStyle, onStyleSelect }: Props) {
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'AlertTriangle':
        return <AlertTriangle className="w-6 h-6" />;
      case 'Sprout':
        return <Sprout className="w-6 h-6" />;
      case 'Network':
        return <Network className="w-6 h-6" />;
      case 'Laugh':
        return <Laugh className="w-6 h-6" />;
      default:
        return null;
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Your Analysis Style</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {analysisStyles.map((style) => (
          <button
            key={style.id}
            onClick={() => onStyleSelect(style.id)}
            className={`flex items-start p-4 rounded-lg border-2 transition-all ${
              selectedStyle === style.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
            }`}
          >
            <div className={`mr-4 ${
              selectedStyle === style.id ? 'text-blue-500' : 'text-gray-400'
            }`}>
              {getIcon(style.icon)}
            </div>
            <div className="text-left">
              <h4 className={`font-medium ${
                selectedStyle === style.id ? 'text-blue-700' : 'text-gray-900'
              }`}>
                {style.name}
              </h4>
              <p className="text-sm text-gray-600 mt-1">{style.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export { analysisStyles };